import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import BarChart from '@mui/icons-material/esm/BarChart';
import Insights from '@mui/icons-material/esm/Insights';
import Timer from '@mui/icons-material/esm/Timer';
import { KPICard } from '../../components/common';
import { PageHeader } from '../../components/layout';
import { RootState } from '../../store';
import defectService, { DefectAnalytics } from '../../services/api/defectService';

const maxRows = <T,>(rows: T[], limit = 8) => rows.slice(0, limit);

interface DefectAnalyticsPageProps {
  embedded?: boolean;
}

export default function DefectAnalyticsPage({ embedded = false }: DefectAnalyticsPageProps = {}) {
  const activeProjectId = useSelector((state: RootState) => state.projects.activeProjectId);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [data, setData] = useState<DefectAnalytics | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!activeProjectId) return;
    setLoading(true);
    setError(null);
    defectService.getAnalytics({ projectId: activeProjectId, from: from || undefined, to: to || undefined })
      .then(setData)
      .catch((err) => setError(err.response?.data?.error || err.response?.data?.message || 'Failed to load defect analytics'))
      .finally(() => setLoading(false));
  }, [activeProjectId, from, to]);

  if (!activeProjectId) {
    return (
      <Box>
        {!embedded && <PageHeader title="Defect Analytics" />}
        <Alert severity="info">Select a project to view defect analytics.</Alert>
      </Box>
    );
  }

  const total = data ? Object.values(data.byStatus).reduce((sum, value) => sum + Number(value), 0) : 0;
  const criticalHigh = data ? Number(data.bySeverity.critical ?? 0) + Number(data.bySeverity.high ?? 0) : 0;

  return (
    <Box>
      {!embedded && <PageHeader title="Defect Analytics" subtitle={data ? `${total} defects in view` : undefined} />}
      <Stack direction="row" spacing={2} sx={{ mb: 2 }} flexWrap="wrap">
        <TextField size="small" type="date" label="From" InputLabelProps={{ shrink: true }} value={from} onChange={(event) => setFrom(event.target.value)} />
        <TextField size="small" type="date" label="To" InputLabelProps={{ shrink: true }} value={to} onChange={(event) => setTo(event.target.value)} />
      </Stack>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading || !data ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
      ) : (
        <>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} md={4}><KPICard title="Defects" value={`${total}`} icon={<Insights />} color="primary" /></Grid>
            <Grid item xs={12} md={4}><KPICard title="Critical / High" value={`${criticalHigh}`} icon={<BarChart />} color={criticalHigh > 0 ? 'error' : 'success'} /></Grid>
            <Grid item xs={12} md={4}><KPICard title="Mean Close Time" value={data.meanTimeToClose.overallDays === null ? '-' : `${data.meanTimeToClose.overallDays}d`} icon={<Timer />} color="info" /></Grid>
          </Grid>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 2 }}>
            <ListPanel title="By Status" rows={Object.entries(data.byStatus).map(([name, count]) => ({ name, count }))} />
            <ListPanel title="By Severity" rows={Object.entries(data.bySeverity).map(([name, count]) => ({ name, count }))} />
            <ListPanel title="By Team" rows={maxRows(data.byTeam.map((row) => ({ name: row.teamName, count: row.count })))} />
            <ListPanel title="By Task Type" rows={maxRows(data.byTaskType.map((row) => ({ name: row.taskType, count: row.count })))} />
            <ListPanel title="Monthly Trend" rows={data.trendByMonth.map((row) => ({ name: row.month, count: `${row.raised} raised / ${row.closed} closed` }))} />
          </Box>
        </>
      )}
    </Box>
  );
}

function ListPanel({ title, rows }: { title: string; rows: Array<{ name: string; count: number | string }> }) {
  return (
    <Paper sx={{ p: 2, borderRadius: 1 }}>
      <Typography variant="h6" fontWeight={700} gutterBottom>{title}</Typography>
      <Stack spacing={1}>
        {rows.map((row) => (
          <Box key={row.name} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
            <Typography variant="body2">{row.name.replace(/_/g, ' ')}</Typography>
            <Typography variant="body2" fontWeight={700}>{row.count}</Typography>
          </Box>
        ))}
        {rows.length === 0 && <Typography variant="body2" color="text.secondary">No data</Typography>}
      </Stack>
    </Paper>
  );
}
